// 第三方库
import axios from 'axios';

// 内部模块 - 工具
import { logger } from 'src/utils/logger';

// 内部模块 - Store
import { loadingStore } from '../store/LoadingStore';
import { accountStore } from '../store/AccountStore';

// 不需要租户ID的接口白名单
const NO_TENANT_ID_WHITELIST = [
  '/erp/auth/login', // ERP登录接口
  'auth/login', // 通用登录接口匹配
];

// ERP认证相关错误关键词
const ERP_AUTH_ERROR_KEYWORDS = [
  '授权信息错误',
  '系统异常',
  'Token',
  '认证',
  '登录',
  'unauthorized',
  'forbidden',
];

// 创建 axios 实例
const request = axios.create({
  // 所有环境都使用相对路径，让代理处理
  baseURL: '',
  timeout: 300000, // 增加到 5 分钟
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
request.interceptors.request.use(
  config => {
    // 从 AccountStore 响应式状态中获取 token 和租户ID
    let tenantId = null;
    let token = null;

    if (accountStore.erpUserInfo) {
      tenantId = accountStore.erpUserInfo.tenantId;
      token = accountStore.erpUserInfo.token;
    }

    // 设置 Authorization 头部
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 为所有请求添加租户信息和应用标识
    config.headers['x-app-id'] = 'chatim';
    config.headers['x-app-secret'] = 'chatim-webapp-secret';

    // 检查是否为不需要租户ID的接口
    const isNoTenantIdRequest = NO_TENANT_ID_WHITELIST.some(pattern => {
      // 检查完整URL或相对路径是否匹配
      return (
        config.url.includes(pattern) ||
        config.url.endsWith(pattern) ||
        config.url.includes('/auth/login')
      );
    });

    if (tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    } else if (!isNoTenantIdRequest) {
      // 只有非白名单接口才需要租户ID
      logger.warn('[Request] 未找到租户ID，请求头中不会包含 x-tenant-id:', { url: config.url });
      // 不要立即登出，而是记录警告
      console.warn('未找到租户ID，请求头中不会包含 x-tenant-id:', { url: config.url });
    }

    // ERP 请求使用统一的 token 处理逻辑

    // 检查是否显示 loading，通过 X-Show-Loading header 控制
    const showLoading = config.headers['X-Show-Loading'] !== 'false';
    if (
      showLoading &&
      [
        '/api/erp/v1/view/pageList',
        '/api/chat/v1/message/list-unread',
        '/api/chat/v1/message/list-send-status',
      ].indexOf(config.url) === -1
    ) {
      loadingStore.startLoading(config.url, '请求中...');
    }
    return config;
  },
  error => {
    // 检查是否显示 loading
    const showLoading = error.config?.headers?.['X-Show-Loading'] !== 'false';
    if (showLoading) {
      loadingStore.stopLoading(error.config?.url);
    }
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  response => {
    const { data } = response;
    // 检查是否显示 loading，通过 X-Show-Loading header 控制
    const showLoading = response.config.headers['X-Show-Loading'] !== 'false';
    if (showLoading) {
      // 结束 loading
      loadingStore.stopLoading(response.config.url);
    }
    // 如果是 mock 响应，直接返回
    if (response.config.__mockResponse) {
      return data;
    }
    // 如果响应直接就是数据，直接返回
    if (!data || typeof data !== 'object') {
      return response;
    }

    // 检查响应中是否包含新的 token，如果有则自动更新
    accountStore.tokenManager.checkAndUpdateToken(data);

    // 处理不同的响应格式
    if (data.code === 0 || data.success) {
      return data;
    }
    // 处理业务错误
    if (data.code === 200) {
      return Promise.reject(new Error('接口返回200，请检查接口实现'));
    }
    // 统一抛出后端 message，交给业务层处理
    if (data.msg || data.message) {
      const err = new Error(data.msg || data.message);
      err.response = response;
      err.code = data.code;
      err.data = data.data;

      // 检查是否为ERP认证相关错误
      const errorMessage = data.msg || data.message;
      if (errorMessage && ERP_AUTH_ERROR_KEYWORDS.some(keyword => errorMessage.includes(keyword))) {
        logger.debug('[ERP认证错误] 检测到ERP认证相关错误，直接登出:', errorMessage);
        accountStore.logout('认证失败，请重新登录');
        return Promise.reject(err);
      }

      return Promise.reject(err);
    }
    // 如果没有错误信息，直接返回数据
    return data;
  },
  error => {
    // 检查是否显示 loading
    const showLoading = error.config?.headers?.['X-Show-Loading'] !== 'false';
    if (showLoading) {
      // 结束 loading
      loadingStore.stopLoading(error.config?.url);
    }

    // 处理 401 错误 - token 过期
    if (error.response?.status === 401) {
      logger.debug('[Token过期] 检测到 401 错误，尝试刷新 token');

      // 避免重复刷新
      if (error.config._retry) {
        logger.debug('[Token过期] 已经尝试过刷新，直接登出');
        accountStore.logout('登录已过期，请重新登录');
        return Promise.reject(error);
      }

      // 标记为已重试
      error.config._retry = true;

      // 尝试刷新 token
      return accountStore
        .refreshToken()
        .then(refreshSuccess => {
          if (refreshSuccess) {
            logger.debug('[Token刷新] 刷新成功，重新发送请求');
            // 更新请求头中的 token
            if (accountStore.erpUserInfo?.token) {
              error.config.headers.Authorization = `Bearer ${accountStore.erpUserInfo.token}`;
            }
            // 重新发送请求
            return request(error.config);
          } else {
            logger.debug('[Token刷新] 刷新失败，登出用户');
            accountStore.logout('登录已过期，请重新登录');
            return Promise.reject(error);
          }
        })
        .catch(refreshError => {
          logger.error('[Token刷新] 刷新过程中出错:', refreshError);
          accountStore.logout('登录已过期，请重新登录');
          return Promise.reject(error);
        });
    }

    // 处理 HTTP 错误，优先传递后端 message
    if (error.response && error.response.data) {
      const msg = error.response.data.msg || error.response.data.message;
      if (msg) {
        error.message = msg;
      }
    }
    return Promise.reject(error);
  }
);

/**
 * 构造 GET 请求的 URL，自动排除空值参数
 * @param {string} baseUrl - 基础路径
 * @param {Object} params - 查询参数
 * @returns {string} 完整的 URL 字符串
 */
export const buildGetUrl = (baseUrl, params) => {
  const url = new URL(baseUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

// 封装请求方法
export const http = {
  get: (url, params) => request.get(url, { params }),
  post: (url, data, config) => request.post(url, data, config),
  put: (url, data) => request.put(url, data),
  delete: url => request.delete(url),
};

export default request;
